import { useEffect, useRef, useState } from "react";

function CameraCapture({ value, onChange, helperText }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!value) {
      setPreviewUrl("");
      return undefined;
    }

    const url = URL.createObjectURL(value);
    setPreviewUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [value]);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  useEffect(() => stopCamera, []);

  const openCamera = async () => {
    setError("");

    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Camera is not available on this device or browser.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });

      streamRef.current = stream;
      setIsCameraOpen(true);

      window.setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 0);
    } catch {
      setError("Unable to open camera. Please allow camera permission or upload a photo.");
    }
  };

  const closeCamera = () => {
    stopCamera();
    setIsCameraOpen(false);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const extension = blob.type === "image/png" ? "png" : "jpg";
        const file = new File([blob], `survey-photo-${Date.now()}.${extension}`, {
          type: blob.type,
          lastModified: Date.now(),
        });
        onChange(file);
        closeCamera();
      },
      "image/jpeg",
      0.92
    );
  };

  return (
    <div className="space-y-3">
      {previewUrl && (
        <div className="overflow-hidden rounded-xl border border-border bg-surface">
          <img
            src={previewUrl}
            alt="Captured evidence"
            className="h-48 w-full object-cover"
          />
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={openCamera}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-600"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h2.25L8.7 5.55A2 2 0 0110.11 5h3.78a2 2 0 011.41.55L16.75 7H19a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {value ? "Retake Photo" : "Open Camera"}
        </button>

        <label className="inline-flex cursor-pointer items-center justify-center rounded-xl border border-border bg-surface px-4 py-2.5 text-sm font-semibold hover:bg-gray-50">
          Upload Photo
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            onChange={(event) => onChange(event.target.files?.[0] || null)}
          />
        </label>

        {value && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="inline-flex items-center justify-center rounded-xl border border-border bg-surface px-4 py-2.5 text-sm font-semibold text-error hover:bg-red-50"
          >
            Remove
          </button>
        )}
      </div>

      {helperText && <p className="text-xs text-text-muted">{helperText}</p>}
      {error && <p className="text-sm font-medium text-error">{error}</p>}

      {isCameraOpen && (
        <div className="fixed inset-0 z-[60] bg-black/70 px-4 py-6 flex items-center justify-center">
          <div className="w-full max-w-2xl rounded-2xl bg-surface border border-border shadow-xl overflow-hidden">
            <div className="flex items-center justify-between gap-4 border-b border-border px-4 py-3">
              <h4 className="text-base font-bold">Capture Photo</h4>
              <button
                type="button"
                onClick={closeCamera}
                className="rounded-lg border border-border px-3 py-1.5 text-sm font-semibold hover:bg-gray-50"
              >
                Close
              </button>
            </div>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="aspect-video w-full bg-black object-cover"
            />
            <div className="flex justify-end gap-3 border-t border-border px-4 py-3">
              <button
                type="button"
                onClick={capturePhoto}
                className="rounded-xl bg-accent px-5 py-2.5 text-sm font-semibold text-white hover:bg-accent-hover"
              >
                Capture
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CameraCapture;
